/**
 * Taxonomy slice — Redux Toolkit state for storefront category navigation.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   cascade: TaxonomyCascadeDTO | null,   // product taxonomy tree
 *   cascadeStatus: 'idle' | 'loading' | 'succeeded' | 'failed',
 *   cascadeError: string | null,
 *   cascadeFetchedAt: number | null,       // wall-clock ms — used for 5-min TTL
 *
 *   resolvedPaths: Record<string, TaxonomyPathDTO>,  // keyed by "slug:path"
 *   resolvedPathStatus: Record<string, 'loading' | 'succeeded' | 'failed'>,
 *   resolvedPathError: Record<string, string | null>,
 *
 *   publicCategories: Record<string, PublicCategoryDTO>,  // keyed by slugPath
 *   publicCategoryStatus: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>,
 *   publicCategoryError: Record<string, string | null>,
 *   publicCategoryHttpStatus: Record<string, number>,
 * }
 * ```
 *
 * Cascade caching: `fetchCategoryCascade` guards on a 5-minute TTL so
 * the header mega-menu fires at most one request per session window. A
 * consumer can force a refresh by dispatching with `force: true`.
 *
 * Path caching: `fetchCategoryByPath` is similarly guarded so navigating
 * back to an already-resolved category path is free.
 *
 * `fetchPublicCategory` calls `GET /api/v1/categories/{slugPath}` — the
 * first-class categories domain endpoint (distinct from the taxonomy-path
 * endpoint). Results are cached per slug path.
 *
 * Architecture: containers dispatch thunks and read selectors; capsules
 * receive plain props. SF-NAV-001.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import * as taxonomiesApiModule from '@dfq/shared/api/taxonomies';
import * as categoriesApiModule from '@dfq/shared/api/categories';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  TaxonomyCascadeDTO,
  TaxonomyPathDTO,
  PublicCategoryDTO,
} from '@dfq/shared/types/taxonomy';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface TaxonomyState {
  cascade: TaxonomyCascadeDTO | null;
  cascadeStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  cascadeError: string | null;
  /** Wall-clock ms of last successful cascade fetch. */
  cascadeFetchedAt: number | null;

  /** Resolved path DTOs keyed by `"<slug>:<path>"`. */
  resolvedPaths: Record<string, TaxonomyPathDTO>;
  resolvedPathStatus: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  resolvedPathError: Record<string, string | null>;
  /** HTTP status code from a failed path fetch (e.g. 404). Keyed by `"<slug>:<path>"`. */
  resolvedPathHttpStatus: Record<string, number>;

  /** Public categories fetched via `GET /api/v1/categories/{slugPath}`. Keyed by slugPath. */
  publicCategories: Record<string, PublicCategoryDTO>;
  publicCategoryStatus: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  publicCategoryError: Record<string, string | null>;
  /** HTTP status code from a failed category fetch (e.g. 404). Keyed by slugPath. */
  publicCategoryHttpStatus: Record<string, number>;
}

const initialState: TaxonomyState = {
  cascade: null,
  cascadeStatus: 'idle',
  cascadeError: null,
  cascadeFetchedAt: null,
  resolvedPaths: {},
  resolvedPathStatus: {},
  resolvedPathError: {},
  resolvedPathHttpStatus: {},
  publicCategories: {},
  publicCategoryStatus: {},
  publicCategoryError: {},
  publicCategoryHttpStatus: {},
};

/** Cache TTL in milliseconds (5 minutes). */
const CASCADE_TTL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchCategoryCascade = createAsyncThunk<
  TaxonomyCascadeDTO,
  { slug: string; force?: boolean },
  { rejectValue: string; state: { taxonomy: TaxonomyState } }
>(
  'taxonomy/fetchCascade',
  async ({ slug }, { rejectWithValue }) => {
    try {
      return await taxonomiesApiModule.getCategoryCascade(slug);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load category navigation';
      return rejectWithValue(message);
    }
  },
  {
    condition: ({ force }, { getState }) => {
      if (force) return true;
      const { taxonomy } = getState();
      if (taxonomy.cascadeStatus === 'loading') return false;
      if (
        taxonomy.cascadeStatus === 'succeeded' &&
        taxonomy.cascadeFetchedAt !== null &&
        Date.now() - taxonomy.cascadeFetchedAt < CASCADE_TTL_MS
      ) {
        return false;
      }
      return true;
    },
  },
);

export const fetchCategoryByPath = createAsyncThunk<
  { key: string; result: TaxonomyPathDTO },
  { slug: string; path: string; force?: boolean },
  { rejectValue: { key: string; message: string; httpStatus: number }; state: { taxonomy: TaxonomyState } }
>(
  'taxonomy/fetchByPath',
  async ({ slug, path }, { rejectWithValue }) => {
    const key = `${slug}:${path}`;
    try {
      const result = await taxonomiesApiModule.getCategoryByPath(slug, path);
      return { key, result };
    } catch (err) {
      const httpStatus = err instanceof ApiError ? err.status : 500;
      const message = err instanceof ApiError ? err.message : 'Category not found';
      return rejectWithValue({ key, message, httpStatus });
    }
  },
  {
    condition: ({ slug, path, force }, { getState }) => {
      if (force) return true;
      const key = `${slug}:${path}`;
      const { taxonomy } = getState();
      if (taxonomy.resolvedPathStatus[key] === 'loading') return false;
      if (taxonomy.resolvedPathStatus[key] === 'succeeded' && taxonomy.resolvedPaths[key]) {
        return false;
      }
      return true;
    },
  },
);

export const fetchPublicCategory = createAsyncThunk<
  { slugPath: string; category: PublicCategoryDTO },
  { slugPath: string; force?: boolean },
  { rejectValue: { slugPath: string; message: string; httpStatus: number }; state: { taxonomy: TaxonomyState } }
>(
  'taxonomy/fetchPublicCategory',
  async ({ slugPath }, { rejectWithValue }) => {
    try {
      const category = await categoriesApiModule.getCategory(slugPath);
      return { slugPath, category };
    } catch (err) {
      const httpStatus = err instanceof ApiError ? err.status : 500;
      const message = err instanceof ApiError ? err.message : 'Category not found';
      return rejectWithValue({ slugPath, message, httpStatus });
    }
  },
  {
    condition: ({ slugPath, force }, { getState }) => {
      if (force) return true;
      const { taxonomy } = getState();
      if (taxonomy.publicCategoryStatus[slugPath] === 'loading') return false;
      if (
        taxonomy.publicCategoryStatus[slugPath] === 'succeeded' &&
        taxonomy.publicCategories[slugPath]
      ) {
        return false;
      }
      return true;
    },
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const taxonomySlice = createSlice({
  name: 'taxonomy',
  initialState,
  reducers: {
    /** Clear the cascade cache (forces the next consumer to re-fetch). */
    clearCascadeCache(state) {
      state.cascade = null;
      state.cascadeStatus = 'idle';
      state.cascadeError = null;
      state.cascadeFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    // ── cascade ──────────────────────────────────────────────────────────
    builder
      .addCase(fetchCategoryCascade.pending, (state) => {
        state.cascadeStatus = 'loading';
        state.cascadeError = null;
      })
      .addCase(fetchCategoryCascade.fulfilled, (state, action) => {
        state.cascade = action.payload;
        state.cascadeStatus = 'succeeded';
        state.cascadeFetchedAt = Date.now();
      })
      .addCase(fetchCategoryCascade.rejected, (state, action) => {
        state.cascadeStatus = 'failed';
        state.cascadeError = action.payload ?? 'Unknown error';
      });

    // ── path resolution ───────────────────────────────────────────────────
    builder
      .addCase(fetchCategoryByPath.pending, (state, action) => {
        const key = `${action.meta.arg.slug}:${action.meta.arg.path}`;
        state.resolvedPathStatus[key] = 'loading';
        state.resolvedPathError[key] = null;
        delete state.resolvedPathHttpStatus[key];
      })
      .addCase(fetchCategoryByPath.fulfilled, (state, action) => {
        const { key, result } = action.payload;
        state.resolvedPaths[key] = result;
        state.resolvedPathStatus[key] = 'succeeded';
        state.resolvedPathError[key] = null;
        delete state.resolvedPathHttpStatus[key];
      })
      .addCase(fetchCategoryByPath.rejected, (state, action) => {
        const key = action.payload?.key ?? `${action.meta.arg.slug}:${action.meta.arg.path}`;
        state.resolvedPathStatus[key] = 'failed';
        state.resolvedPathError[key] = action.payload?.message ?? 'Unknown error';
        state.resolvedPathHttpStatus[key] = action.payload?.httpStatus ?? 500;
      });

    // ── public category (categories domain endpoint) ──────────────────────
    builder
      .addCase(fetchPublicCategory.pending, (state, action) => {
        const { slugPath } = action.meta.arg;
        state.publicCategoryStatus[slugPath] = 'loading';
        state.publicCategoryError[slugPath] = null;
        delete state.publicCategoryHttpStatus[slugPath];
      })
      .addCase(fetchPublicCategory.fulfilled, (state, action) => {
        const { slugPath, category } = action.payload;
        state.publicCategories[slugPath] = category;
        state.publicCategoryStatus[slugPath] = 'succeeded';
        state.publicCategoryError[slugPath] = null;
        delete state.publicCategoryHttpStatus[slugPath];
      })
      .addCase(fetchPublicCategory.rejected, (state, action) => {
        const slugPath = action.payload?.slugPath ?? action.meta.arg.slugPath;
        state.publicCategoryStatus[slugPath] = 'failed';
        state.publicCategoryError[slugPath] = action.payload?.message ?? 'Unknown error';
        state.publicCategoryHttpStatus[slugPath] = action.payload?.httpStatus ?? 500;
      });
  },
});

export const { clearCascadeCache } = taxonomySlice.actions;
export const taxonomyReducer = taxonomySlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const selectTaxonomySlice = (state: { taxonomy: TaxonomyState }) => state.taxonomy;

export const selectCascade = createSelector(
  selectTaxonomySlice,
  (s) => s.cascade,
);

export const selectCascadeStatus = createSelector(
  selectTaxonomySlice,
  (s) => s.cascadeStatus,
);

export const selectCascadeError = createSelector(
  selectTaxonomySlice,
  (s) => s.cascadeError,
);

export const makeSelectCategoryByPath = (slug: string, path: string) =>
  createSelector(selectTaxonomySlice, (s) => {
    const key = `${slug}:${path}`;
    return {
      data: s.resolvedPaths[key] ?? null,
      status: s.resolvedPathStatus[key] ?? 'idle',
      error: s.resolvedPathError[key] ?? null,
      httpStatus: s.resolvedPathHttpStatus[key] ?? null,
    };
  });

export const makeSelectPublicCategory = (slugPath: string) =>
  createSelector(selectTaxonomySlice, (s) => ({
    data: s.publicCategories[slugPath] ?? null,
    status: s.publicCategoryStatus[slugPath] ?? 'idle',
    error: s.publicCategoryError[slugPath] ?? null,
    httpStatus: s.publicCategoryHttpStatus[slugPath] ?? null,
  }));

// ---------------------------------------------------------------------------
// Re-export types for slice consumers
// ---------------------------------------------------------------------------

export type {
  TaxonomyCascadeDTO,
  TaxonomyPathDTO,
  TaxonomyNodeDTO,
  ProductCascadeCategoryDTO,
  ProductCascadeMaterialDTO,
  ProductCascadeVehicleSurfaceDTO,
  PublicCategoryDTO,
  PublicCategoryPathDTO,
} from '@dfq/shared/types/taxonomy';
